// Tesla Window Sticker Style PDF Template for Simulators
// Landscape orientation with clean, structured sections

class TeslaSimulatorPDFTemplate {
  constructor() {
    this.doc = null;
    // Landscape A4 dimensions
    this.pageWidth = 297; // Landscape width
    this.pageHeight = 210; // Landscape height
         this.margins = {
       top: 15,
       right: 15,
       bottom: 15,
       left: 15
     };
    this.contentWidth = this.pageWidth - this.margins.left - this.margins.right;
    this.currentY = this.margins.top;
    
    // Tesla minimalist colors - only black and white
    this.colors = {
      black: [0, 0, 0],
      lightGray: [200, 200, 200],
      white: [255, 255, 255]
    };
    
         // Clean typography - optimized for space
     this.fonts = {
       header: { size: 11, font: 'helvetica', style: 'bold' },
       title: { size: 9, font: 'helvetica', style: 'bold' },
       body: { size: 8, font: 'helvetica', style: 'normal' },
       price: { size: 12, font: 'helvetica', style: 'bold' },
       small: { size: 7, font: 'helvetica', style: 'normal' }
     };
    
    // 3-column layout positions
    this.columns = {
      left: { x: this.margins.left, width: 85 },
      middle: { x: this.margins.left + 90, width: 85 },
      right: { x: this.margins.left + 180, width: 85 }
    };
  }

  // Initialize PDF document with landscape orientation and compression
  init(jsPDFConstructor) {
    this.doc = new jsPDFConstructor({
      orientation: 'landscape',
      unit: 'mm',
      format: 'a4',
      compress: true,
      precision: 2
    });
    this.doc.setFillColor(...this.colors.white);
    this.doc.rect(0, 0, this.pageWidth, this.pageHeight, 'F');
    return this;
  }

  // Optimize image for PDF compression while keeping visual quality
  optimizeImageForPDF(imageUrl) {
    return new Promise((resolve, reject) => {
      if (!imageUrl) {
        resolve(null);
        return;
      }

      // 📱 MOBILE DETECTION: Skip complex image operations only on actual mobile browsers
      const isMobileBrowser = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      
      if (isMobileBrowser) {
        console.log('📱 Mobile browser detected: Skipping image optimization for compatibility');
        resolve(imageUrl); // Use original image on mobile browsers to avoid canvas issues
        return;
      }
      
      console.log('🖥️ Desktop/tablet browser: Proceeding with image optimization');

      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();
      
      // Set timeout for image loading to prevent hanging on mobile
      const timeout = setTimeout(() => {
        console.warn('⏰ Image optimization timeout, using original');
        resolve(imageUrl);
      }, 5000); // 5 second timeout
      
      img.onload = () => {
        clearTimeout(timeout);
        
        try {
          // Set canvas to reasonable resolution for PDF
          const maxWidth = 400; // Reduced from source for compression
          const maxHeight = 300;
          
          let { width, height } = img;
          
          // Maintain aspect ratio while reducing size
          if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          }
          if (height > maxHeight) {
            width = (width * maxHeight) / height;
            height = maxHeight;
          }
          
          canvas.width = width;
          canvas.height = height;
          
          // Draw and compress
          ctx.drawImage(img, 0, 0, width, height);
          
          // For PNG images (likely logos), preserve PNG format to maintain transparency
          // For other images, use JPEG compression
          const isPNG = imageUrl.toLowerCase().includes('.png') || imageUrl.toLowerCase().includes('logo');
          let compressedDataUrl;
          
          if (isPNG) {
            // Keep PNG format to preserve transparency for logos
            compressedDataUrl = canvas.toDataURL('image/png');
          } else {
            // Convert to compressed JPEG data URL (60% quality for good compression)
            compressedDataUrl = canvas.toDataURL('image/jpeg', 0.6);
          }
          
          resolve(compressedDataUrl);
        } catch (canvasError) {
          console.warn('Canvas operation failed, using original:', canvasError);
          resolve(imageUrl);
        }
      };
      
      img.onerror = () => {
        clearTimeout(timeout);
        console.warn('Could not load image, using original:', imageUrl);
        resolve(imageUrl); // Fallback to original
      };
      
      img.crossOrigin = 'anonymous';
      img.src = imageUrl;
    });
  }

     // Add clean header like Tesla window sticker
   addHeader(modelName, date, logoUrl = null, quoteNumber = null) {
     if (logoUrl) {
       try {
         // Add company logo on the left (same size, compressed)
         const logoWidth = 55; // Keep maximum impact size
         const logoHeight = 15; // Keep proportional
         // Use PNG format for logos to preserve transparency
         const imageFormat = logoUrl.toLowerCase().includes('.png') || logoUrl.toLowerCase().includes('logo') ? 'PNG' : 'JPEG';
         this.doc.addImage(logoUrl, imageFormat, this.margins.left, this.margins.top - 8, logoWidth, logoHeight);
       } catch (e) {
         console.error('Error adding logo:', e);
         // Fallback to text if logo fails to load
         this.doc.setFont(this.fonts.header.font, this.fonts.header.style);
         this.doc.setFontSize(this.fonts.header.size);
         this.doc.setTextColor(...this.colors.black);
         this.doc.text('SIM COACHES', this.margins.left, this.margins.top);
       }
     } else {
       // SIM COACHES text fallback (if no logo provided)
       this.doc.setFont(this.fonts.header.font, this.fonts.header.style);
       this.doc.setFontSize(this.fonts.header.size);
       this.doc.setTextColor(...this.colors.black);
       this.doc.text('SIM COACHES', this.margins.left, this.margins.top);
     }
     
     // Model name in center
     this.doc.setFont(this.fonts.header.font, this.fonts.header.style);
     this.doc.setFontSize(this.fonts.header.size);
     this.doc.setTextColor(...this.colors.black);
     this.doc.text(modelName, this.pageWidth / 2, this.margins.top, { align: 'center' });
     
     // Date and Quote Number on right side
     this.doc.setFont(this.fonts.small.font, this.fonts.small.style);
     this.doc.setFontSize(this.fonts.small.size);
     this.doc.setTextColor(...this.colors.black);
     this.doc.text(`Generated: ${date}`, this.pageWidth - this.margins.right, this.margins.top, { align: 'right' });
     
     // Quote Number prominently displayed below date
     if (quoteNumber) {
       this.doc.setFont(this.fonts.body.font, 'bold');
       this.doc.setFontSize(this.fonts.body.size);
       this.doc.setTextColor(...this.colors.black);
       this.doc.text(`Quote #: ${quoteNumber}`, this.pageWidth - this.margins.right, this.margins.top + 6, { align: 'right' });
     }
     
                    this.currentY = this.margins.top + 12; // Increased spacing to accommodate quote number
     return this;
   }

  // Add simulator image in left column (same size, compressed for PDF optimization)
  addSimulatorImage(imageUrl) {
    if (!imageUrl) {
      console.log('PDF Template - No image URL provided to addSimulatorImage');
      return this;
    }
    
    console.log('PDF Template - Adding simulator image, URL type:', typeof imageUrl);
    console.log('PDF Template - Image URL length:', imageUrl.length);
    console.log('PDF Template - Image URL starts with data:', imageUrl.startsWith('data:'));
    
    try {
             // Image dimensions - keep exactly the same for beautiful layout
       const imgWidth = 70;
       const imgHeight = 40;
      const imgX = this.columns.left.x;
      const imgY = this.currentY;
      
      // Simple thin border
      this.doc.setDrawColor(...this.colors.black);
      this.doc.setLineWidth(0.5);
      this.doc.rect(imgX, imgY, imgWidth, imgHeight, 'D');
      
      // Determine image format from data URL or default to JPEG
      let imageFormat = 'JPEG';
      if (imageUrl.startsWith('data:image/png')) {
        imageFormat = 'PNG';
      } else if (imageUrl.startsWith('data:image/jpeg') || imageUrl.startsWith('data:image/jpg')) {
        imageFormat = 'JPEG';
      }
      
      console.log('PDF Template - Using image format:', imageFormat);
      
      // Add image to PDF
      this.doc.addImage(imageUrl, imageFormat, imgX + 2, imgY + 2, imgWidth - 4, imgHeight - 4, undefined, 'MEDIUM');
      
      console.log('PDF Template - Simulator image added successfully');
      
    } catch (e) {
      console.error('PDF Template - Error adding simulator image:', e);
      console.error('PDF Template - Image URL that failed:', imageUrl.substring(0, 100) + '...');
    }
    
    return this;
  }

     // Create the main 3-column content layout
   addConfiguration(configData) {
     let leftY = this.currentY + 45; // Reduced space below image
     let middleY = this.currentY;
     let rightY = this.currentY;
     
     // Add base model price above center and right columns
     this.addBaseModelHeader(configData, this.columns.middle.x, middleY);
     middleY += 12; // Reduced space after header
     rightY += 12;
    
                                                       // LEFT COLUMN: Chassis, Computer, Screens (moved from right), Seat, Direct Drive Steering Base (moved from middle)
        leftY = this.addColumnSection('CHASSIS', this.getChassisItems(configData), this.columns.left.x, leftY);
        leftY = this.addComputerSection(configData, this.columns.left.x, leftY + 2);
        leftY = this.addColumnSection('SCREENS', this.getScreensItems(configData), this.columns.left.x, leftY + 2);
        leftY = this.addColumnSection('SEAT', this.getSeatItems(configData), this.columns.left.x, leftY + 2);
        
        // Add MOTION section for OMEGA and ELITE simulators only
        const simulatorType = this.getSimulatorType(configData);
        if (simulatorType === 'omega' || simulatorType === 'elite') {
          leftY = this.addColumnSection('MOTION', this.getMotionItems(configData), this.columns.left.x, leftY + 2);
        }
        
        leftY = this.addColumnSection('DIRECT DRIVE STEERING BASE', this.getSteeringBaseItems(configData), this.columns.left.x, leftY + 2);
        
        // MIDDLE COLUMN: Steering Wheel, Pedals, Shifter, Handbrake, Software
        middleY = this.addColumnSection('STEERING WHEEL', this.getSteeringWheelItems(configData), this.columns.middle.x, middleY);
        middleY = this.addColumnSection('PEDALS', this.getPedalsItems(configData), this.columns.middle.x, middleY + 2);
        middleY = this.addColumnSection('SHIFTER', this.getShifterItems(configData), this.columns.middle.x, middleY + 2);
        middleY = this.addColumnSection('HANDBRAKE', this.getHandbrakeItems(configData), this.columns.middle.x, middleY + 2);
        middleY = this.addColumnSection('SOFTWARE', this.getSoftwareItems(configData), this.columns.middle.x, middleY + 2);
      
        // RIGHT COLUMN: Haptics, Flight Controls, Additional Accessories, Games, Shipping, Installation
       rightY = this.addColumnSection('HAPTICS', this.getHapticsItems(configData), this.columns.right.x, rightY);
       rightY = this.addColumnSection('FLIGHT CONTROLS', this.getFlightControlsItems(configData), this.columns.right.x, rightY + 2);
       rightY = this.addColumnSection('ADDITIONAL ACCESSORIES', this.getAdditionalAccessoriesItems(configData), this.columns.right.x, rightY + 2);
       rightY = this.addColumnSection('GAMES', this.getGamesItems(configData), this.columns.right.x, rightY + 2);
       rightY = this.addColumnSection('SHIPPING', this.getShippingItems(configData), this.columns.right.x, rightY + 2);
       rightY = this.addColumnSection('INSTALLATION', this.getInstallationItems(configData), this.columns.right.x, rightY + 2);
       rightY = this.addColumnSection('TRAINING', this.getTrainingItems(configData), this.columns.right.x, rightY + 2);
    
                   // Update current Y position to be below all columns
      this.currentY = Math.max(leftY, middleY, rightY) + 4;
    
    return this;
  }

  // Add base model header above center and right columns
  addBaseModelHeader(configData, startX, startY) {
    const basePrice = configData.baseModel ? configData.baseModel.price : 0;
    const modelName = configData.baseModel ? configData.baseModel.name : 'Base Model';
    
    // Base model name - show screen version without (1)
    this.doc.setFont(this.fonts.title.font, this.fonts.title.style);
    this.doc.setFontSize(this.fonts.title.size);
    this.doc.setTextColor(...this.colors.black);
    this.doc.text(`Base Model: ${modelName}`, startX, startY);
    
    // Price on the right side
    this.doc.text(this.formatPrice(basePrice), this.pageWidth - this.margins.right, startY, { align: 'right' });
    
    // Black underline spanning both middle and right columns
    this.doc.setDrawColor(...this.colors.black);
    this.doc.setLineWidth(0.5);
    this.doc.line(startX, startY + 2, this.pageWidth - this.margins.right, startY + 2);
    
    return this;
  }

     // Add computer section with PC stage in title
   addComputerSection(configData, startX, startY) {
     // Use the consistent simulator type detection method
     const simulatorType = this.getSimulatorType(configData);
     
     // Get computer items using the same structure as other sections
     const computerItems = this.getComputerItems(simulatorType);
     
     // Use addColumnSection to maintain consistency
     return this.addColumnSection('COMPUTER', computerItems, startX, startY);
   }

     // Helper to add a column section
   addColumnSection(title, items, startX, startY) {
     let yPos = startY;
     
     // Determine which column we're in and get the correct width
     let columnWidth = 85; // Default
     if (startX === this.columns.left.x) {
       columnWidth = this.columns.left.width;
     } else if (startX === this.columns.middle.x) {
       columnWidth = this.columns.middle.width;
     } else if (startX === this.columns.right.x) {
       columnWidth = this.columns.right.width;
     }
     
                               // Determine if section should show FREE or INCLUDED
        let sectionLabel = '';
        if (title === 'SHIPPING' || title === 'INSTALLATION' || title === 'TRAINING') {
          sectionLabel = 'FREE';
        } else if (title === 'DIRECT DRIVE STEERING BASE' || title === 'SCREENS' || title === 'HAPTICS' || title === 'CHASSIS' || title === 'SEAT' || title === 'COMPUTER' || title === 'MOTION' || title === 'SOFTWARE' || title === 'GAMES') {
          sectionLabel = 'INCLUDED';
        }
     
           // Section title with label
      this.doc.setFont(this.fonts.title.font, this.fonts.title.style);
      this.doc.setFontSize(this.fonts.title.size);
      this.doc.setTextColor(...this.colors.black);
      
      // Always place title on the left
      this.doc.text(title, startX, yPos);
      
      if (sectionLabel) {
        // Add label aligned to the right side of the column
        this.doc.setFont(this.fonts.body.font, 'bold');
        this.doc.setFontSize(8);
        this.doc.text(sectionLabel, startX + columnWidth, yPos, { align: 'right' });
        
        // Reset font
        this.doc.setFont(this.fonts.title.font, this.fonts.title.style);
        this.doc.setFontSize(this.fonts.title.size);
      }
     
                     // Black underline
       this.doc.setDrawColor(...this.colors.black);
       this.doc.setLineWidth(0.3);
       this.doc.line(startX, yPos + 1, startX + columnWidth, yPos + 1);
       yPos += 4;
     
     // Items
     this.doc.setFont(this.fonts.body.font, this.fonts.body.style);
     this.doc.setFontSize(this.fonts.body.size);
     this.doc.setTextColor(...this.colors.black);
     
            items.forEach(item => {
         if (yPos > this.pageHeight - 25) return; // Reduced overflow protection
       
               if (typeof item === 'string') {
          // Check if item already has bullet point
          const text = item.startsWith('•') ? item : `• ${item}`;
          yPos = this.addWrappedText(text, startX, yPos, columnWidth);
          yPos += 1; // Reduced spacing between items
       } else if (item.name) {
         // Item name with pricing on the right - no wrapping, single line
         const itemText = `• ${item.name}`;
         this.doc.text(itemText, startX, yPos);
         yPos += 3;
         
                   // Add pricing next to the item name if it has a price or special flags
          if (item.isIncluded) {
            // Show INCLUDED for special items like Velo wheel
            this.doc.setFont(this.fonts.body.font, 'bold');
            this.doc.setFontSize(8);
            this.doc.text('INCLUDED', startX + columnWidth - 5, yPos - 3, { align: 'right' });
            
            // Reset font
            this.doc.setFont(this.fonts.body.font, this.fonts.body.style);
            this.doc.setFontSize(this.fonts.body.size);
          } else if (item.price > 0) {
            if (item.isFree) {
              // Show crossed out price and FREE
              const priceText = this.formatPrice(item.price);
              this.doc.setFont(this.fonts.body.font, 'normal');
              this.doc.setFontSize(8);
              this.doc.text(priceText, startX + columnWidth - 25, yPos - 3, { align: 'right' });
              // Draw line through the price
              const priceWidth = this.doc.getTextWidth(priceText);
              this.doc.line(startX + columnWidth - 25 - priceWidth, yPos - 4, startX + columnWidth - 25, yPos - 4);
              // Show FREE
              this.doc.setFont(this.fonts.body.font, 'bold');
              this.doc.setFontSize(8);
              this.doc.text('FREE', startX + columnWidth - 5, yPos - 3, { align: 'right' });
            } else {
              // Show regular pricing
              const priceText = this.formatPrice(item.price);
              this.doc.setFont(this.fonts.body.font, 'bold');
              this.doc.setFontSize(8);
              this.doc.text(priceText, startX + columnWidth - 5, yPos - 3, { align: 'right' });
            }
            
            // Reset font
            this.doc.setFont(this.fonts.body.font, this.fonts.body.style);
            this.doc.setFontSize(this.fonts.body.size);
          }
         
         // Add features under the item with different styling
         if (item.features && Array.isArray(item.features)) {
                       item.features.forEach(feature => {
              if (yPos > this.pageHeight - 25) return; // Reduced overflow protection
             
                           // Feature text with offset and different color
              this.doc.setTextColor(100, 100, 100); // Gray color for features
              this.doc.setFont(this.fonts.body.font, this.fonts.body.style);
              this.doc.setFontSize(7);
              
                             const featureText = feature.startsWith('•') ? `  ${feature}` : `  • ${feature}`;
               yPos = this.addWrappedText(featureText, startX + 3, yPos, columnWidth - 18); // Wider container for features
              
              // Reset color
              this.doc.setTextColor(...this.colors.black);
              this.doc.setFontSize(this.fonts.body.size);
            });
          }
          
          yPos += 1.5; // Reduced spacing between items
       }
     });
     
     return yPos;
   }

     
   // Helper method to add text with proper wrapping within column constraints
   addWrappedText(text, startX, startY, maxWidth) {
    let yPos = startY;
    
         // Use jsPDF's splitTextToSize to wrap text within column width
     // Leave 5mm margin for safety - optimized for better space utilization
     const lines = this.doc.splitTextToSize(text, maxWidth - 5);
    
    // Debug logging for long text
    if (lines.length > 1) {
      console.log('PDF Template - Wrapping text:', text);
      console.log('PDF Template - Split into lines:', lines);
      console.log('PDF Template - Max width:', maxWidth - 8);
    }
    
                          lines.forEach((line, index) => {
         if (yPos > this.pageHeight - 25) return; // Reduced overflow protection
        
        // Trim any extra whitespace from wrapped lines
        const trimmedLine = line.trim();
        if (trimmedLine.length > 0) {
          this.doc.text(trimmedLine, startX, yPos);
          yPos += 2.5; // Further reduced line spacing
        }
      });
    
    return yPos;
  }

  // Get items for each section based on configuration
           getChassisItems(configData) {
      const items = [
        {
          name: 'P1 Pro Racing Chassis by Sim Coaches',
          price: 0, // Included
          features: [
            'Aluminum Construction | Zero Flex',
            'CAD-Engineered | Lifetime Warranty'
          ]
        }
      ];
     
     // Add any additional chassis selections
     if (configData.selectedOptions) {
       Object.entries(configData.selectedOptions).forEach(([groupId, selection]) => {
         if (groupId.toLowerCase().includes('chassis') || groupId.toLowerCase().includes('frame')) {
           const selectionArray = Array.isArray(selection) ? selection : [selection];
           selectionArray.forEach(item => {
             if (item && item.name) {
               // Convert item to structured format if needed
               if (typeof item === 'object' && item.name) {
                 items.push(item);
               } else {
                 items.push({
                   name: item.name || item,
                   price: item.price || 0,
                   features: []
                 });
               }
             }
           });
         }
       });
     }
     return items;
   }

           getSeatItems(configData) {
      const items = [
        {
          name: 'Sparco Evo XL Racing Seat',
          price: 0, // Included
          features: [
            '38" Waist Size | FIA Certified | F1 Heritage',
            '6-way Adjustable Rails | Pro Racing Standard'
          ]
        }
      ];
     
     // Add any additional seat selections
     if (configData.selectedOptions) {
       Object.entries(configData.selectedOptions).forEach(([groupId, selection]) => {
         if (groupId.toLowerCase().includes('seat')) {
           const selectionArray = Array.isArray(selection) ? selection : [selection];
           selectionArray.forEach(item => {
             if (item && item.name) {
               // Convert item to structured format if needed
               if (typeof item === 'object' && item.name) {
                 items.push(item);
               } else {
                 items.push({
                   name: item.name || item,
                   price: item.price || 0,
                   features: []
                 });
               }
             }
           });
         }
       });
     }
     return items;
   }

           getSteeringBaseItems(configData) {
      const items = [
        {
          name: 'Simucube 2 Pro Direct Drive Wheelbase',
          price: 0, // Included
          features: [
            '25Nm Peak Torque | Pro Racing Standard',
            '1M PPR Resolution | Instant Force Feedback',
            'CNC Machined | Telemetry Ready'
          ]
        }
      ];
     
     // Add any additional steering base selections
     if (configData.selectedOptions) {
       Object.entries(configData.selectedOptions).forEach(([groupId, selection]) => {
         if (groupId.toLowerCase().includes('wheelbase') || groupId.toLowerCase().includes('steering') && groupId.toLowerCase().includes('base')) {
           const selectionArray = Array.isArray(selection) ? selection : [selection];
           selectionArray.forEach(item => {
             if (item && item.name) {
               // Convert item to structured format if needed
               if (typeof item === 'object' && item.name) {
                 items.push(item);
               } else {
                 items.push({
                   name: item.name || item,
                   price: item.price || 0,
                   features: []
                 });
               }
             }
           });
         }
       });
     }
     return items;
   }

     getComputerItems(simulatorType) {
     const isElite = simulatorType === 'elite';
     const pcStage = isElite ? 'Stage 3' : 'Stage 2';
     
     const items = [
       {
         name: `${pcStage} Gaming PC`,
         price: 0, // Included
         features: isElite ? [
           'AMD Ryzen 7 9800X3D | RTX 5080',
           '32GB DDR5 RAM | 4TB NVMe SSD',
           '1200W PSU | AIO Liquid Cooling | RGB',
           'Windows 11 Pro | VR Ready (8K Support)'
         ] : [
           'AMD Ryzen 7 9800X3D | RTX 5070',
           '32GB DDR5 RAM | 2TB NVMe SSD',
           '1000W PSU | AIO Liquid Cooling | RGB',
           'Windows 11 Pro | VR Ready (4K/8K)'
         ]
       }
     ];
     
     return items;
   }

           getSteeringWheelItems(configData) {
      const items = [];
      
      // Add user-selected wheels with pricing and specific features
      if (configData.selectedOptions) {
        Object.entries(configData.selectedOptions).forEach(([groupId, selection]) => {
          if (groupId.toLowerCase().includes('wheel') && !groupId.toLowerCase().includes('base')) {
            const selectionArray = Array.isArray(selection) ? selection : [selection];
            selectionArray.forEach(item => {
              if (item && item.name) {
                // Generate features based on wheel type
                let features = [];
                const wheelName = item.name.toLowerCase();
                
                                                  if (wheelName.includes('simucube') && wheelName.includes('valo')) {
                   features = [
                     '320mm Alcantara Rim | 18 RGB LEDs',
                     '2000mAh Wireless | Zero Cables Required'
                   ];
                 } else if (wheelName.includes('simucube') && wheelName.includes('tahko')) {
                   features = [
                     '320mm Steel GT-Rim | Suede Fabric Finish',
                     'Wireless Technology | Magnetic Paddles'
                   ];
                 } else if (wheelName.includes('gsi') && (wheelName.includes('gt') || wheelName.includes('max'))) {
                   features = [
                     'GT-Sport Professional Racing Wheel',
                     'CNC Machined Aluminum | Premium Materials'
                   ];
                 } else if (wheelName.includes('mclaren') && wheelName.includes('artura')) {
                   if (wheelName.includes('ultimate')) {
                     features = [
                       'Official McLaren GT4 Components | 4" Dashboard',
                       '300mm Diameter | Gen6 Magnetic Shifters'
                     ];
                   } else if (wheelName.includes('pro')) {
                     features = [
                       'Official McLaren GT4 Components | McLaren Licensed',
                       '300mm Diameter | Motorsport-Grade Construction'
                     ];
                   } else if (wheelName.includes('sport')) {
                     features = [
                       'Official McLaren GT4 Components | McLaren Licensed',
                       '300mm Diameter | Magnetic Single Shifters'
                     ];
                   } else {
                     features = [
                       'Official McLaren GT4 Components | McLaren Licensed',
                       '300mm Diameter | Professional Racing Wheel'
                     ];
                   }
                 } else {
                   // Default features for other wheels
                   features = [
                     'Professional Racing Wheel Design',
                     'High-Quality Construction | Racing Performance'
                   ];
                 }
                 
                                   // Check if this is a Simucube Valo wheel to mark as included
                  const isValo = wheelName.includes('simucube') && wheelName.includes('valo');
                  
                  items.push({
                    name: item.name,
                    price: item.price || 0,
                    features: features,
                    isIncluded: isValo // Mark Valo wheels as included
                  });
              }
            });
          }
        });
      }
      
      // If no wheels selected, show standard
      if (items.length === 0) {
        items.push({
          name: 'Premium Racing Steering Wheel',
          price: 0,
          features: [
            'F1 Magnetic Paddle Shifters | Function Buttons',
            'Precision Rotary Encoders | Real Car Feel'
          ]
        });
      }
      
      return items;
    }

  getPedalsItems(configData) {
    let pedalSelection = null;

    if (configData.selectedOptions) {
      Object.entries(configData.selectedOptions).forEach(([groupId, selection]) => {
        if (!groupId.toLowerCase().includes('pedal')) return;
        const selectionArray = Array.isArray(selection) ? selection : [selection];
        selectionArray.forEach(item => {
          if (item && item.name) {
            pedalSelection = item;
          }
        });
      });
    }

    const pedalName = pedalSelection?.name?.toLowerCase() || '';
    const isInverted = pedalName.includes('inverted');
    const isThreePedalUpgrade = pedalName.includes('3-pedal');

    return [
      {
        name: isInverted
          ? (isThreePedalUpgrade ? 'Inverted P1 Pro Hydraulic Pedals - Inverted 3-Pedal' : 'Inverted P1 Pro Hydraulic Pedals')
          : (isThreePedalUpgrade ? 'P1 Pro Hydraulic Sim Racing Pedals - Standard 3-Pedal' : 'P1 Pro Hydraulic Sim Racing Pedals'),
        price: pedalSelection ? (pedalSelection.price || 0) : 0,
        features: isInverted
          ? [
              'Professional Inverted Formula Style',
              'Hydraulic Brake System | 0-1600PSI Range'
            ]
          : [
              'Real Hydraulic Brake System | 0-1600PSI Range',
              'Fully CNC Aluminum | Lifetime Warranty'
            ]
      }
    ];
  }

     getHapticsItems(configData) {
     const items = [];
     
     // Use the consistent simulator type detection method
     const simulatorType = this.getSimulatorType(configData);
     
     // Debug logging for haptics specifically
     console.log('PDF Template - Haptics - Detected simulator type:', simulatorType);
    
    if (simulatorType === 'pro' || simulatorType === 'omega') {
      items.push({
        name: '100W Earthquake Shaker Kit by Sim Coaches',
        price: 0,
        features: [
          '2 Channel Left/Right Output',
          'Calibrated to Engine RPM, Gear Shifts, Curbing'
        ]
      });
    } else if (simulatorType === 'elite') {
      items.push({
        name: '600W Earthquake Pro Shaker Kit by Sim Coaches',
        price: 0,
        features: [
          '2 Channel Left/Right Output',
          'Calibrated to Engine RPM, Gear Shifts, Curbing'
        ]
      });
    } else {
      items.push({
        name: '100W Earthquake Shaker Kit by Sim Coaches',
        price: 0,
        features: [
          '2 Channel Left/Right Output',
          'Calibrated to Engine RPM, Gear Shifts, Curbing'
        ]
      });
    }
    
    // Add any additional haptic selections
    if (configData.selectedOptions) {
      Object.entries(configData.selectedOptions).forEach(([groupId, selection]) => {
        if (groupId.toLowerCase().includes('haptic') || groupId.toLowerCase().includes('vibration')) {
          const selectionArray = Array.isArray(selection) ? selection : [selection];
          selectionArray.forEach(item => {
            if (item && item.name) items.push(item);
          });
        }
      });
    }
    
    return items;
  }

  getScreensItems(configData) {
    const items = [];
    
    // Show the selected model as the screen configuration
    if (configData.baseModel && configData.baseModel.name) {
      const modelName = configData.baseModel.name;
      let features = [];
      
      // Add value description based on model type
      if (modelName.toLowerCase().includes('single')) {
        features = [
          'High-Quality Single Monitor | Immersive Racing'
        ];
      } else if (modelName.toLowerCase().includes('triple') || modelName.toLowerCase().includes('3')) {
        features = [
          'Triple Monitor Ultra-Wide | 180° Field of View',
          'Professional Racing Setup | Maximum Immersion'
        ];
      } else if (modelName.toLowerCase().includes('ultra-wide') || modelName.toLowerCase().includes('ultrawide')) {
        features = [
          'Ultra-Wide Curved Display | Enhanced Vision',
          'Premium Gaming Experience | Seamless Flow'
        ];
      } else {
        features = [
          'Premium Monitor Configuration | Pro Display'
        ];
      }
      
      items.push({
        name: modelName,
        price: 0, // Included
        features: features
      });
    } else {
      items.push({
        name: 'Monitor Setup',
        price: 0, // Included
        features: [
          'Premium Monitor Configuration | Pro Display'
        ]
      });
    }
    
    // Add any additional screen selections
    if (configData.selectedOptions) {
      Object.entries(configData.selectedOptions).forEach(([groupId, selection]) => {
        if (groupId.toLowerCase().includes('screen') || groupId.toLowerCase().includes('monitor') || groupId.toLowerCase().includes('display')) {
          const selectionArray = Array.isArray(selection) ? selection : [selection];
          selectionArray.forEach(item => {
            if (item && item.name) items.push(item);
          });
        }
      });
    }
    
    return items;
  }

     getFlightControlsItems(configData) {
     const items = [];
     let hasFlightControls = false;
     
     if (configData.selectedOptions) {
       Object.entries(configData.selectedOptions).forEach(([groupId, selection]) => {
         if (groupId.toLowerCase().includes('flight') || groupId.toLowerCase().includes('joystick') || groupId.toLowerCase().includes('throttle')) {
           const selectionArray = Array.isArray(selection) ? selection : [selection];
           selectionArray.forEach(item => {
             if (item && item.name) {
               hasFlightControls = true;
               
               // Generate features based on flight control type
               let features = [];
               const itemName = item.name.toLowerCase();
               
              if (itemName.includes('helicopter')) {
                features = [
                  'Collective Pitch | Anti-Torque Pedals',
                  'Cyclic Stick | Military-Grade Components'
                ];
              } else if (itemName.includes('fighter') || itemName.includes('pilot')) {
                features = [
                  'Military-Spec Joystick | Throttle Quadrant',
                  'Combat Flight Training Ready'
                ];
              } else {
                features = [
                  'Professional Flight Controls',
                  'Military-Grade Components'
                ];
              }
               
               items.push({
                 name: item.name,
                 price: item.price || 0,
                 features: features
               });
             }
           });
         }
       });
     }
     
     return hasFlightControls ? items : ['None Selected'];
   }

                       getShifterItems(configData) {
       const items = [];
       
       // Debug logging
       console.log('PDF Template - Shifter Detection - selectedOptions:', configData.selectedOptions);
       
       if (configData.selectedOptions) {
         Object.entries(configData.selectedOptions).forEach(([groupId, selection]) => {
           console.log('PDF Template - Checking groupId:', groupId, 'selection:', selection);
           
           const selectionArray = Array.isArray(selection) ? selection : [selection];
           selectionArray.forEach(item => {
             if (item && item.name) {
               const itemName = item.name.toLowerCase();
               const groupIdLower = groupId.toLowerCase();
               
               // Broader detection logic - check for any shifter-related keywords.
               // Screen/display groups are excluded: "LG OLED UltraGear" contains
               // "gear" and was landing monitors in the shifter section.
               const isScreenGroup = groupIdLower.includes('screen') ||
                                groupIdLower.includes('display') ||
                                groupIdLower.includes('monitor');
               const isShifter = !isScreenGroup && (
                                groupIdLower.includes('shifter') ||
                                groupIdLower.includes('gear') ||
                                groupIdLower.includes('transmission') ||
                                itemName.includes('shifter') ||
                                itemName.includes('mme') ||
                                itemName.includes('bdh') ||
                                itemName.includes('h-pattern') ||
                                itemName.includes('sequential') ||
                                (itemName.includes('gear') && !itemName.includes('ultragear')));
               
               console.log('PDF Template - Item:', itemName, 'isShifter:', isShifter);
               
               if (isShifter) {
                 console.log('PDF Template - Adding shifter item:', item.name);
                 
                 // Convert to structured format if needed
                 if (typeof item === 'object' && item.name) {
                   // If item already has features, use as-is, otherwise add default features
                   if (!item.features || item.features.length === 0) {
                     // Generate features based on shifter type
                     let features = [];
                     if (itemName.includes('mme') || itemName.includes('all in one')) {
                       features = [
                         'All-in-One H-Pattern + Sequential',
                         'Professional Racing Shifter'
                       ];
                     } else if (itemName.includes('bdh') || itemName.includes('h-pattern') || itemName.includes('sequential')) {
                       features = [
                         'H-Pattern + Sequential Shifter',
                         'Realistic Racing Experience'
                       ];
                     } else {
                       features = [
                         'Professional Racing Shifter',
                         'Authentic Gear Changes'
                       ];
                     }
                     
                     items.push({
                       name: item.name,
                       price: item.price || 0,
                       features: features
                     });
                   } else {
                     items.push(item);
                   }
                 } else {
                   // Default features for shifters
                   items.push({
                     name: item.name || item,
                     price: item.price || 0,
                     features: [
                       'Professional Racing Shifter',
                       'Authentic Gear Changes'
                     ]
                   });
                 }
               }
             }
           });
         });
       }
       
               // Fallback: If no items found, search through all selections for any shifter-related terms
        if (items.length === 0 && configData.selectedOptions) {
          console.log('PDF Template - Shifter fallback search activated');
          Object.entries(configData.selectedOptions).forEach(([groupId, selection]) => {
            const selectionArray = Array.isArray(selection) ? selection : [selection];
            selectionArray.forEach(item => {
              if (item && item.name) {
                const itemName = item.name.toLowerCase();
                // Very broad search for shifter terms (UltraGear monitors excluded)
                if (itemName.includes('shift') || (itemName.includes('gear') && !itemName.includes('ultragear')) || itemName.includes('mme') || itemName.includes('bdh')) {
                  console.log('PDF Template - Fallback found shifter:', item.name);
                  items.push({
                    name: item.name,
                    price: item.price || 0,
                    features: [
                      'Professional Racing Shifter',
                      'Authentic Gear Changes'
                    ]
                  });
                }
              }
            });
          });
        }
        
        console.log('PDF Template - Final shifter items:', items);
       return items.length > 0 ? items : ['None Selected'];
     }

           getHandbrakeItems(configData) {
      const items = [];
      
      // Debug logging
      console.log('PDF Template - Handbrake Detection - selectedOptions:', configData.selectedOptions);
      
      if (configData.selectedOptions) {
        Object.entries(configData.selectedOptions).forEach(([groupId, selection]) => {
          console.log('PDF Template - Checking groupId:', groupId, 'selection:', selection);
          
          const selectionArray = Array.isArray(selection) ? selection : [selection];
          selectionArray.forEach(item => {
            if (item && item.name) {
              const itemName = item.name.toLowerCase();
              const groupIdLower = groupId.toLowerCase();
              
              // Broader detection logic - check for any handbrake-related keywords
              const isHandbrake = groupIdLower.includes('handbrake') || 
                                 groupIdLower.includes('hand-brake') ||
                                 groupIdLower.includes('brake') ||
                                 groupIdLower.includes('ebrake') ||
                                 groupIdLower.includes('e-brake') ||
                                 itemName.includes('handbrake') ||
                                 itemName.includes('hand-brake') ||
                                 itemName.includes('brake') ||
                                 itemName.includes('ebrake') ||
                                 itemName.includes('e-brake') ||
                                 itemName.includes('p1 pro');
              
              console.log('PDF Template - Item:', itemName, 'isHandbrake:', isHandbrake);
              
              if (isHandbrake) {
                console.log('PDF Template - Adding handbrake item:', item.name);
                
                // Convert to structured format if needed
                if (typeof item === 'object' && item.name) {
                  // If item already has features, use as-is, otherwise add default features
                  if (!item.features || item.features.length === 0) {
                    items.push({
                      name: item.name,
                      price: item.price || 0,
                      features: [
                        'Professional Rally Handbrake',
                        'Authentic Racing Experience'
                      ]
                    });
                  } else {
                    items.push(item);
                  }
                } else {
                  items.push({
                    name: item.name || item,
                    price: item.price || 0,
                    features: [
                      'Professional Rally Handbrake',
                      'Authentic Racing Experience'
                    ]
                  });
                }
              }
            }
          });
        });
      }
      
              // Fallback: If no items found, search through all selections for any handbrake-related terms
        if (items.length === 0 && configData.selectedOptions) {
          console.log('PDF Template - Handbrake fallback search activated');
          Object.entries(configData.selectedOptions).forEach(([groupId, selection]) => {
            const selectionArray = Array.isArray(selection) ? selection : [selection];
            selectionArray.forEach(item => {
              if (item && item.name) {
                const itemName = item.name.toLowerCase();
                // Very broad search for brake terms
                if (itemName.includes('brake') || itemName.includes('handbrake') || itemName.includes('hand brake') || itemName.includes('p1')) {
                  console.log('PDF Template - Fallback found handbrake:', item.name);
                  items.push({
                    name: item.name,
                    price: item.price || 0,
                    features: [
                      'Professional Rally Handbrake',
                      'Authentic Racing Experience'
                    ]
                  });
                }
              }
            });
          });
        }
        
        console.log('PDF Template - Final handbrake items:', items);
       return items.length > 0 ? items : ['None Selected'];
    }

     getShippingItems(configData) {
     const items = [
       {
         name: 'FREE Freight Shipping within the USA',
         price: 0,
         isFree: true,
         features: [
           'Fully Insured | White-Glove Service',
           'Scheduled Delivery | No Hidden Costs'
         ]
       }
     ];
     return items;
   }

       getInstallationItems(configData) {
      const items = [
        {
          name: 'Professional Installation',
          price: 0,
          isFree: true,
          features: [
            'Expert Technician Setup | System Calibration',
            'Complete Hardware Installation | Software Setup'
          ]
        }
      ];
      return items;
    }

    getTrainingItems(configData) {
      const items = [
        {
          name: 'Personal Training Session',
          price: 0,
          isFree: true,
          features: [
            'One-on-One Racing Instruction',
            'Simulator Setup | Lifetime Technical Support'
          ]
        }
      ];
      return items;
    }

    getSoftwareItems(configData) {
      // Tier follows the simulator: Elite ships with PRO 20X, Pro/Omega with PRO 5X
      const type = (this.getSimulatorType(configData) || '').toLowerCase();
      const isElite = type.includes('elite');
      const items = [
        isElite ? {
          name: 'TrackPro PRO 20X — First Year Included',
          price: 0,
          features: [
            '$3,564 Value | $297/mo After Year One',
            'AI Race Coach On Every Session | Human Lap Reviews',
            'Lifetime Warranty+ | Race Pass | Driver Lab | Telemetry'
          ]
        } : {
          name: 'TrackPro PRO 5X — First Year Included',
          price: 0,
          features: [
            '$1,164 Value | $97/mo After Year One',
            'AI Race Coach In Your Ear | Monthly Human Lap Reviews',
            'Race Pass | Driver Lab | Telemetry | Communities'
          ]
        }
      ];
      return items;
    }

    getGamesItems(configData) {
      const items = [
        {
          name: 'iRacing Subscription',
          price: 0,
          features: ['2-Year Membership | $300+ Value | Official Series']
        },
        {
          name: 'Assetto Corsa Deluxe Edition',
          price: 0,
          features: ['Full Game + All DLC | Content Manager']
        }
      ];
      let hasFlightControls = false;
      if (configData.selectedOptions) {
        Object.keys(configData.selectedOptions).forEach(groupId => {
          if (groupId.toLowerCase().includes('flight')) {
            const selection = configData.selectedOptions[groupId];
            if (Array.isArray(selection) ? selection.length > 0 : selection) {
              hasFlightControls = true;
            }
          }
        });
      }
      if (hasFlightControls) {
        items.push({
          name: 'Microsoft Flight Simulator',
          price: 0,
          features: ['Premium Deluxe Edition | Full World Map']
        });
        items.push({
          name: 'Digital Combat Simulator',
          price: 0,
          features: ['Military Flight Simulation | Multiplayer']
        });
      }
      return items;
    }

           getAdditionalAccessoriesItems(configData) {
      const items = [];
      if (configData.selectedOptions) {
        Object.entries(configData.selectedOptions).forEach(([groupId, selection]) => {
          // Catch any remaining accessories not categorized above
          const excludeGroups = ['chassis', 'frame', 'seat', 'wheelbase', 'steering', 'wheel', 'pedal', 'haptic', 'vibration', 'screen', 'monitor', 'display', 'flight', 'joystick', 'throttle', 'shifter', 'gear', 'handbrake', 'brake'];
          const isGroupExcluded = excludeGroups.some(exclude => groupId.toLowerCase().includes(exclude));
          
          if (!isGroupExcluded) {
            const selectionArray = Array.isArray(selection) ? selection : [selection];
            selectionArray.forEach(item => {
              if (item && item.name) {
                // Also check item names to exclude shifters and handbrakes that might not be caught by groupId
                const itemName = item.name.toLowerCase();
                const isItemExcluded = itemName.includes('shifter') || 
                                      itemName.includes('handbrake') || 
                                      itemName.includes('mme all in one') ||
                                      itemName.includes('bdh h-pattern') ||
                                      itemName.includes('sequential shifter') ||
                                      itemName.includes('gear') ||
                                      itemName.includes('brake');
                
                if (!isItemExcluded) {
                  // Check for specific accessories and add detailed specs
                  if (itemName.includes('qubic') && itemName.includes('belt')) {
                    items.push({
                      name: item.name,
                      price: item.price || 0,
                      features: [
                        'Dual-Channel Direct Drive | 200N Per Channel',
                        'Professional Racing Safety | Motion-Lock System'
                      ]
                    });
                  } else if (itemName.includes('digital dashboard') || itemName.includes('dashboard')) {
                    items.push({
                      name: item.name,
                      price: item.price || 0,
                      features: [
                        '4.3" High-Resolution Display | RPM LED Strip',
                        'Custom Car Displays | Real-Time Telemetry'
                      ]
                    });
                  } else {
                    items.push(item);
                  }
                }
              }
            });
          }
        });
      }
      
             // Only show accessories that are actually selected - no hardcoded defaults
      
            return items.length > 0 ? items : ['None Selected'];
    }

         // Helper method to get simulator type
     getSimulatorType(configData) {
       // Method 1: Check URL path (check most specific first)
       const currentPath = window.location.pathname.toLowerCase();
       console.log('PDF Template - URL path:', currentPath);
       
       if (currentPath.includes('elite')) {
         console.log('PDF Template - Detected ELITE from URL');
         return 'elite';
       } else if (currentPath.includes('omega')) {
         console.log('PDF Template - Detected OMEGA from URL');
         return 'omega';
       } else if (currentPath.includes('pro')) {
         console.log('PDF Template - Detected PRO from URL');
         return 'pro';
       }
       
       // Method 2: Check configData for product title (fallback)
       if (configData.productTitle) {
         const title = configData.productTitle.toLowerCase();
         console.log('PDF Template - Product title:', title);
         
         if (title.includes('elite')) {
           console.log('PDF Template - Detected ELITE from product title');
           return 'elite';
         } else if (title.includes('omega')) {
           console.log('PDF Template - Detected OMEGA from product title');
           return 'omega';
         } else if (title.includes('pro')) {
           console.log('PDF Template - Detected PRO from product title');
           return 'pro';
         }
       }
       
       // Method 3: Check base model name for type detection
       if (configData.baseModel && configData.baseModel.name) {
         const modelName = configData.baseModel.name.toLowerCase();
         console.log('PDF Template - Base model name:', modelName);
         
         if (modelName.includes('elite')) {
           console.log('PDF Template - Detected ELITE from base model');
           return 'elite';
         } else if (modelName.includes('omega')) {
           console.log('PDF Template - Detected OMEGA from base model');
           return 'omega';
         } else if (modelName.includes('pro')) {
           console.log('PDF Template - Detected PRO from base model');
           return 'pro';
         }
       }
       
       console.log('PDF Template - No simulator type detected, defaulting to PRO');
       console.log('PDF Template - Full configData for debugging:', JSON.stringify(configData, null, 2));
       return 'pro'; // Default to pro instead of empty string
     }

    getMotionItems(configData) {
      const simulatorType = this.getSimulatorType(configData);
      const items = [];
      
      if (simulatorType === 'omega') {
        items.push({
          name: '3DOF 6" Motion Kit by Sim Coaches',
          price: 0, // Included
          features: [
            'Professional 3-Axis Motion System',
            'Heave, Pitch, Roll Movement',
            '6" Travel Range | Ultra-Smooth Operation'
          ]
        });
      } else if (simulatorType === 'elite') {
        items.push({
          name: '6DOF 6" Motion Kit by Sim Coaches',
          price: 0, // Included
          features: [
            'Professional 6-Axis Motion System',
            'Heave, Pitch, Roll, Surge Movement',
            'Traction Loss Front & Rear | Elite Performance'
          ]
        });
      }
      
      return items;
    }

      // Add pricing section (like car sticker)
   addPricing(basePrice, totalPrice, msrpPrice = null) {
     // Separator line
     this.doc.setDrawColor(...this.colors.black);
     this.doc.setLineWidth(1);
     this.doc.line(this.margins.left, this.currentY, this.pageWidth - this.margins.right, this.currentY);
     
     this.currentY += 6;
     
     if (msrpPrice && msrpPrice > totalPrice) {
       // SALE DISPLAY
       
       // MSRP Line (Strikethrough)
       this.doc.setFont(this.fonts.body.font, 'normal');
       this.doc.setFontSize(10);
       this.doc.setTextColor(100, 100, 100); // Gray
       this.doc.text('MSRP', this.margins.left, this.currentY);
       
       const msrpText = this.formatPrice(msrpPrice);
       const msrpWidth = this.doc.getTextWidth(msrpText);
       const xPos = this.pageWidth - this.margins.right;
       
       this.doc.text(msrpText, xPos, this.currentY, { align: 'right' });
       // Strikethrough line
       this.doc.setDrawColor(100, 100, 100);
       this.doc.setLineWidth(0.3);
       this.doc.line(xPos - msrpWidth, this.currentY - 1, xPos, this.currentY - 1);
       
       this.currentY += 5;
       
       // Sale Price Line
       this.doc.setFont(this.fonts.price.font, this.fonts.price.style);
       this.doc.setFontSize(14); // Larger
       this.doc.setTextColor(204, 0, 0); // Red
       this.doc.text('CYBER MONDAY SALE PRICE (20% OFF)', this.margins.left, this.currentY);
       this.doc.text(this.formatPrice(totalPrice), this.pageWidth - this.margins.right, this.currentY, { align: 'right' });
       
     } else {
       // STANDARD DISPLAY
       this.doc.setFont(this.fonts.price.font, this.fonts.price.style);
       this.doc.setFontSize(this.fonts.price.size);
       this.doc.setTextColor(...this.colors.black);
       this.doc.text('TOTAL SIMULATOR PRICE', this.margins.left, this.currentY);
       this.doc.text(this.formatPrice(totalPrice), this.pageWidth - this.margins.right, this.currentY, { align: 'right' });
     }
     
     this.currentY += 6;
     
     return this;
   }

     // Add clean, simple footer at the very bottom
   addFooter() {
     const footerY = this.pageHeight - 8; // Positioned to leave minimal space
    
    // Reset any drawing colors and line styles to ensure clean footer
    this.doc.setDrawColor(...this.colors.black);
    this.doc.setLineWidth(0);
    this.doc.setFillColor(...this.colors.white);
    
    // Simple footer text with minimal spacing and clean styling
    this.doc.setFont(this.fonts.small.font, this.fonts.small.style);
    this.doc.setFontSize(this.fonts.small.size);
    this.doc.setTextColor(...this.colors.black);
    
         // Left side: Next step call-to-action
     this.doc.text('NEXT STEP: Email us at sales@simcoaches.com with this sheet to chat about building or call us at (702) 246-2295.', this.margins.left, footerY);
     
     // Right side: Website
     this.doc.text('www.simcoaches.com', this.pageWidth - this.margins.right, footerY, { align: 'right' });
    
    return this;
  }

  // Format price helper - FIXED to handle cents correctly
  formatPrice(priceInCents) {
    // Convert cents to dollars
    const priceInDollars = priceInCents / 100;
    
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(priceInDollars);
  }

           // Main method to generate complete PDF with image optimization
    async generatePDF(configData, simulatorImageData, logoUrl = null) {
      // Debug: Log the entire configData structure
      console.log('PDF Template - Full configData:', JSON.stringify(configData, null, 2));
      
      // Use CRM-generated quote number (Q-YYYY-NNN) if available, otherwise generate fallback
      let quoteNumber;
      if (window.crmQuoteNumber) {
        quoteNumber = window.crmQuoteNumber;
        console.log('PDF Template - Using CRM quote number:', quoteNumber);
      } else {
        const now = new Date();
        const dateStr = now.toISOString().slice(0, 10).replace(/-/g, '');
        const randomSuffix = Math.random().toString(36).substr(2, 4).toUpperCase();
        quoteNumber = `QT-${dateStr}-${randomSuffix}`;
        console.log('PDF Template - CRM quote number not available, using fallback:', quoteNumber);
      }
      
      // Store quote number for external access
      this.quoteNumber = quoteNumber;
      console.log('PDF Template - Generated Quote Number:', quoteNumber);
      
      // Use consistent simulator type detection
      const simulatorTypeRaw = this.getSimulatorType(configData);
      console.log('PDF Template - Detected simulator type:', simulatorTypeRaw);
      
      // Capitalize for display
      const simulatorType = simulatorTypeRaw ? 
        simulatorTypeRaw.charAt(0).toUpperCase() + simulatorTypeRaw.slice(1) : '';
     
     // Set header title based on simulator type
     const modelName = simulatorType ? 
       `${simulatorType} Racing Simulator` : 
       'Racing Simulator';
     
     const date = new Date().toLocaleDateString();
     
     // Optimize images for compression while keeping visual quality
     const optimizedLogo = logoUrl ? await this.optimizeImageForPDF(logoUrl) : null;
     const optimizedSimulatorImage = simulatorImageData ? await this.optimizeImageForPDF(simulatorImageData) : null;
     
     console.log('PDF Template - Images optimized for compression');
     console.log('PDF Template - Original simulator image data:', simulatorImageData ? 'Present' : 'Null');
     console.log('PDF Template - Optimized simulator image:', optimizedSimulatorImage ? 'Present' : 'Null');
     
     // Add all sections with optimized images (including quote number)
     this.addHeader(modelName, date, optimizedLogo, quoteNumber);
     
     if (optimizedSimulatorImage) {
       console.log('PDF Template - Adding simulator image to PDF');
       this.addSimulatorImage(optimizedSimulatorImage);
     } else if (simulatorImageData) {
       console.log('PDF Template - Using original simulator image data (optimization failed)');
       this.addSimulatorImage(simulatorImageData);
     } else {
       console.log('PDF Template - No simulator image available');
     }
     
     this.addConfiguration(configData);
     
     const basePrice = configData.baseModel ? configData.baseModel.price : 0;
     const totalPrice = configData.totalPrice || 0;
     const msrpPrice = configData.msrpPrice || null;
     
     this.addPricing(basePrice, totalPrice, msrpPrice);
     
     this.addFooter();
     
     return this;
   }

  // Save the PDF
  //
  // jsPDF's own save() quietly degrades to opening the blob in a tab on iOS
  // Safari and in-app webviews (Instagram, Facebook, Gmail) — the customer gets
  // a preview with no way to keep the file, so they screenshot it. Drive the
  // download ourselves, and always publish the blob URL so the UI can offer a
  // real link when the browser still refuses.
  save(filename) {
    try {
      const blob = this.doc.output('blob');
      const url = URL.createObjectURL(blob);

      window.scLastBuildSheet = { url: url, filename: filename };

      const link = document.createElement('a');
      if (typeof link.download === 'undefined') {
        // No download attribute (old in-app webviews) — a tab is the only option.
        window.open(url, '_blank');
        document.dispatchEvent(new CustomEvent('sc:build-sheet-ready', {
          detail: { url: url, filename: filename, downloaded: false }
        }));
        return;
      }

      link.href = url;
      link.download = filename;
      link.rel = 'noopener';
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      document.dispatchEvent(new CustomEvent('sc:build-sheet-ready', {
        detail: { url: url, filename: filename, downloaded: true }
      }));

      // Hold the object URL long enough for slow devices to finish writing it.
      setTimeout(function() { URL.revokeObjectURL(url); }, 120000);
    } catch (err) {
      console.error('PDF save fallback:', err);
      this.doc.save(filename);
    }
  }
}

// Export for use in other scripts
window.TeslaSimulatorPDFTemplate = TeslaSimulatorPDFTemplate;

// Keep backward compatibility
window.SimulatorPDFTemplate = TeslaSimulatorPDFTemplate; 